import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { borderRadius } from '@liminal/brand/scale';
import { Button } from '@liminal/web-ui/primitives/Button';
import { IconButton } from '@liminal/web-ui/primitives/IconButton';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { ConfirmationDialog } from '@liminal/web-ui/overlays/ConfirmationDialog';
import { FormErrorBanner } from '@liminal/web-ui/forms/FormErrorBanner';
import { getErrorMessage } from '@/errors/messages';
import { isInFlight, useDeleteRewardMerchantImage, type MerchantImageUploads } from '@/hooks/useMerchantImages';
import { MAX_PHOTOS, type ServedImage } from '@/lib/merchantImages';
import { ImageDropZone } from './ImageDropZone';
import { ImageUploadRow } from './ImageUploadRow';

interface MerchantPhotosFieldProps {
  /** Images need a merchant to belong to, so a partner not yet saved has none. */
  readonly merchantSaved: boolean;
  /** The photos in display order; the first is the main one. */
  readonly images: readonly ServedImage[];
  /** A new order, or a new main photo. */
  readonly onReorder: (images: readonly ServedImage[]) => void;
  /** The image is gone from the server; the form drops it from its list as the list is by then. */
  readonly onDeleted: (name: string) => void;
  readonly uploads: MerchantImageUploads;
  readonly disabled: boolean;
}

/**
 * The partner form's photos: a drop zone, the uploads on their way, and the
 * photos as thumbnails to reorder, make main or delete.
 *
 * The order and the main photo are the form's, written by Save. A delete is
 * not: it goes to the server at once, after a confirm, because it removes
 * the stored file.
 */
export function MerchantPhotosField({ merchantSaved, images, onReorder, onDeleted, uploads, disabled }: MerchantPhotosFieldProps) {
  const deleteImage = useDeleteRewardMerchantImage();
  const [deleting, setDeleting] = useState<{ readonly image: ServedImage; readonly requestId: string } | null>(null);
  const [deleteError, setDeleteError] = useState('');

  if (!merchantSaved) {
    return (
      <Typography variant="body2" color="text.secondary">
        Save the partner first, then add photos.
      </Typography>
    );
  }

  const room = MAX_PHOTOS - images.length - uploads.uploads.filter((upload) => isInFlight(upload.state)).length;

  const move = (from: number, to: number) => {
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  const closeDelete = () => {
    setDeleting(null);
    setDeleteError('');
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteImage.mutateAsync({ name: deleting.image.name, requestId: deleting.requestId });
      onDeleted(deleting.image.name);
      closeDelete();
    } catch (e) {
      // partner-service's own words, such as its refusal to delete the current logo.
      setDeleteError(getErrorMessage(e));
    }
  };

  return (
    <Stack spacing={2}>
      {images.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 2 }}>
          {images.map((image, index) => {
            const position = index + 1;
            return (
              <Stack key={image.name} spacing={1} data-testid={`photo ${position}`}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={image.uri}
                    alt={`Photo ${position}`}
                    sx={{
                      display: 'block',
                      width: '100%',
                      aspectRatio: '4 / 3',
                      objectFit: 'cover',
                      borderRadius: `${borderRadius.md}px`,
                      border: `1px solid ${webColors.border.light}`,
                    }}
                  />
                  {index === 0 && (
                    <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
                      <StatusPill tone="info" label="Main" size="sm" />
                    </Box>
                  )}
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <IconButton
                    icon="chevron-back"
                    label={`Move photo ${position} earlier`}
                    size="sm"
                    tone="muted"
                    disabled={disabled || index === 0}
                    onClick={() => move(index, index - 1)}
                  />
                  <IconButton
                    icon="chevron-forward"
                    label={`Move photo ${position} later`}
                    size="sm"
                    tone="muted"
                    disabled={disabled || index === images.length - 1}
                    onClick={() => move(index, index + 1)}
                  />
                  <Box sx={{ flex: 1 }} />
                  {index > 0 && (
                    <Button variant="link" size="xs" disabled={disabled} onClick={() => move(index, 0)}>
                      Make main
                    </Button>
                  )}
                  <IconButton
                    icon="trash"
                    label={`Delete photo ${position}`}
                    size="sm"
                    tone="danger"
                    disabled={disabled}
                    onClick={() => setDeleting({ image, requestId: crypto.randomUUID() })}
                  />
                </Stack>
              </Stack>
            );
          })}
        </Box>
      )}

      {uploads.uploads.map((upload) => (
        <ImageUploadRow
          key={upload.key}
          upload={upload}
          onRetry={() => uploads.retry(upload.key)}
          onDismiss={() => uploads.dismiss(upload.key)}
        />
      ))}

      {room > 0 ? (
        <ImageDropZone
          label="Add photos"
          prompt={`Drop JPEG, PNG, WebP or SVG photos here, up to ${MAX_PHOTOS} in all. Each is scaled down to 2048 px before it is sent.`}
          buttonLabel="Choose photos"
          multiple
          disabled={disabled}
          onFiles={(files) => uploads.add(files, room)}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          {`A partner has at most ${MAX_PHOTOS} photos. Delete one to add another.`}
        </Typography>
      )}

      <ConfirmationDialog
        open={deleting !== null}
        title="Delete this photo"
        message="It is removed from the partner and from storage now, without waiting for Save. This cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={deleteImage.isPending}
        onConfirm={() => void confirmDelete()}
        onCancel={closeDelete}
      >
        <FormErrorBanner error={deleteError} />
      </ConfirmationDialog>
    </Stack>
  );
}
